import React, { useCallback, useEffect, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChecklistRtlIcon from '@mui/icons-material/ChecklistRtl';

import { getTodos, createTodo, updateTodo, deleteTodo } from '@/services/marketingCalendarService';

const ACCENT = '#c2410c';

const MarketingTodoList = () => {
  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [showDone, setShowDone] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setTodos(await getTodos()); } catch { setTodos([]); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    const value = text.trim();
    if (!value) return;
    setText('');
    await createTodo(value).catch(() => {});
    load();
  };

  const toggle = async (todo) => {
    setTodos((list) => list.map((t) => (t.id === todo.id ? { ...t, is_done: !t.is_done } : t)));
    await updateTodo(todo.id, { is_done: !todo.is_done }).catch(() => {});
    load();
  };

  const remove = async (id) => {
    setTodos((list) => list.filter((t) => t.id !== id));
    await deleteTodo(id).catch(() => {});
  };

  const open = todos.filter((t) => !t.is_done);
  const done = todos.filter((t) => t.is_done);

  const Row = ({ todo, isDone }) => (
    <div className="group flex items-start gap-2 py-1.5 border-b border-gray-50 last:border-b-0">
      <input
        type="checkbox"
        checked={isDone}
        onChange={() => toggle(todo)}
        className={`mt-0.5 w-3.5 h-3.5 rounded flex-shrink-0 cursor-pointer ${isDone ? 'accent-green-600' : 'accent-orange-700'}`}
      />
      <span className={`flex-1 min-w-0 text-sm break-words ${isDone ? 'text-gray-400 line-through' : 'text-gray-700'}`}>
        {todo.text}
      </span>
      <button
        type="button"
        onClick={() => remove(todo.id)}
        className="text-gray-300 hover:text-red-600 opacity-0 group-hover:opacity-100 transition flex-shrink-0"
      >
        <DeleteOutlineIcon style={{ fontSize: 14 }} />
      </button>
    </div>
  );

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex items-center gap-1.5 mb-2 flex-shrink-0">
        <ChecklistRtlIcon style={{ fontSize: 16, color: ACCENT }} />
        <h3 className="text-sm font-bold text-gray-800">My To-Do</h3>
        {open.length > 0 && (
          <span className="text-[10px] font-bold text-white rounded-full px-1.5 py-0.5" style={{ backgroundColor: ACCENT }}>
            {open.length}
          </span>
        )}
      </div>

      <div className="flex gap-1 mb-2 flex-shrink-0">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          placeholder="Add a to-do…"
          className="flex-1 min-w-0 rounded-lg border border-gray-200 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/30"
        />
        <button
          type="button"
          onClick={add}
          disabled={!text.trim()}
          className="px-2 rounded-lg text-white disabled:opacity-40"
          style={{ backgroundColor: ACCENT }}
        >
          <AddIcon style={{ fontSize: 16 }} />
        </button>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto pr-0.5">
        {loading ? (
          <p className="text-xs text-gray-400 italic">Loading…</p>
        ) : (
          <>
            {open.length === 0 && <p className="text-xs text-gray-400 italic">Nothing to do.</p>}
            {open.map((t) => <Row key={t.id} todo={t} isDone={false} />)}

            {done.length > 0 && (
              <>
                <button
                  type="button"
                  onClick={() => setShowDone((v) => !v)}
                  className="flex items-center gap-0.5 mt-3 text-[11px] font-semibold text-gray-400 hover:text-gray-600"
                >
                  {showDone ? <ExpandMoreIcon style={{ fontSize: 14 }} /> : <ChevronRightIcon style={{ fontSize: 14 }} />}
                  Done ({done.length})
                </button>
                {showDone && done.map((t) => <Row key={t.id} todo={t} isDone />)}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MarketingTodoList;
